<!--
@component
Views

See markup below.
-->

<script lang="ts" module>
  type Gallery = { id: string; url?: string };

  export type MobileView = 'commentary' | 'stats' | 'lineups' | 'photos';
</script>

<script lang="ts">
  import { Block } from '@reuters-graphics/graphics-components';

  import { isPlaying, type Match } from '$utils/getMatchStats';
  import type { CommentaryEntry } from '$lib/@types';

  import Commentary from '../Commentary/index.svelte';
  import Lineup from '../Lineup/index.svelte';
  import Stats from '../Stats/index.svelte';
  import Photos from '../Photos/index.svelte';
  import Switch from './Switch.svelte';

  interface Props {
    /**
     * Raw commentary feed; typed as `unknown` because the data layer hands
     * it down without narrowing. We assert to `CommentaryEntry[]` at the
     * boundary where it's passed into <Commentary />.
     */
    commentary: unknown;
    match: Match | undefined;
    matchStats: Array<{ formationUsed?: unknown }> | undefined;
    homeTeam: unknown;
    awayTeam: unknown;
    matchUrl: string;
    /**
     * URL to the JSON manifest of Reuters photo galleries associated with
     * tournament matches. Sourced from page-data content (e.g.
     * `content['galleries-url']`) so the URL can be edited in the
     * storyboard without a code change.
     */
    galleriesUrl?: string;
  }

  let {
    commentary,
    match,
    matchStats,
    homeTeam,
    awayTeam,
    matchUrl,
    galleriesUrl,
  }: Props = $props();

  let galleryUrl = $state<string | undefined>(undefined);

  const isLive = $derived(isPlaying(match));

  const hasStats = $derived(Boolean(matchStats));
  const hasLineups = $derived(
    Boolean(
      matchStats && matchStats[0]?.formationUsed && matchStats[1]?.formationUsed
    )
  );
  const hasCommentary = $derived(Boolean(commentary));
  const hasPhotos = $derived(Boolean(galleryUrl));

  // The default view based on available content. Used to initialise
  // `mobileView` and to fall back when the user-selected view becomes
  // unavailable. NOTE: `mobileView` cannot be `$derived` because
  // Switch.svelte binds to it to write user selections.
  const defaultView = $derived<MobileView | null>(
    hasCommentary ? 'commentary'
    : hasStats ? 'stats'
    : hasPhotos ? 'photos'
    : hasLineups ? 'lineups'
    : null
  );

  let mobileView = $state<MobileView | null>(null);

  // Keep mobileView valid: initialise it, and reset if the currently
  // selected view is no longer available.
  $effect(() => {
    const isAvailable =
      (mobileView === 'commentary' && hasCommentary) ||
      (mobileView === 'stats' && hasStats) ||
      (mobileView === 'photos' && hasPhotos) ||
      (mobileView === 'lineups' && hasLineups);

    if (!isAvailable) mobileView = defaultView;
  });

  $effect(() => {
    const id = matchUrl;
    if (!galleriesUrl) {
      galleryUrl = undefined;
      return;
    }
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch(galleriesUrl, {
          signal: controller.signal,
        });
        const data: Gallery[] = await response.json();
        const gallery = data.find((g) => g.id === id);
        galleryUrl = gallery?.url;
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        galleryUrl = undefined;
      }
    })();

    return () => controller.abort();
  });
</script>

<div id="match-view-scroll-target">
  <Switch bind:mobileView {hasStats} {hasLineups} {hasPhotos} {hasCommentary} />

  {#if mobileView === 'commentary'}
    <Block width="normal" class="fmb-7" id="commentary">
      <Commentary commentary={commentary as CommentaryEntry[]} {isLive} />
    </Block>
  {:else if mobileView === 'stats'}
    <Block width="wide" class="fmy-6">
      <Stats stats={matchStats} />
    </Block>
  {:else if mobileView === 'lineups'}
    <Block width="wide" class="fmy-6">
      <Lineup {matchStats} {homeTeam} {awayTeam} />
    </Block>
  {:else if mobileView === 'photos'}
    <Photos {galleryUrl} />
  {/if}
</div>
